import React from 'react';
import { css } from '@linaria/core';
import { theme, brand, findColorFromCSSVariable } from '@terveystalo/design-system-theme';
import { classnames } from '../../utils';
import { Icon } from '../../atoms/Icon';
import { Text } from '../../atoms/Typography';
import forwardRefAs from 'forward-ref-as';
import { focusVisible } from '../../css-utils/focus-visible';
import { calcPadding3_5 } from '../../css-utils/calculation';
import { isIconRightDirectional } from '../../utils/icons';
/**
 * Use explicit class names to target icons in order avoid conflicts with e.g., svg filters
 */
export const iconLeftClassName = 'item-icon-left';
export const iconRightClassName = 'item-icon-right';
/**
 * Common styles for all list-like items.
 *
 * Used for both `ListItem` and `MenuItem`
 */
export const baseListItemStyles = css `
  box-sizing: border-box;
  width: 100%;
  margin: 0;

  padding: ${calcPadding3_5(theme.typography.lineHeight.body.m, '0_5')};

  @media (hover: hover) {
    padding-left: ${theme.spacing[1]};
    padding-right: ${theme.spacing[1]};
  }

  border: none;
  display: flex;
  text-decoration: none;
  text-align: left;

  /* Remove tap highlight in mobile context for some browsers */
  -webkit-tap-highlight-color: transparent;

  /* Content */
  > span {
    flex: 1;
  }

  /* Left Icon */
  > img.${iconLeftClassName} {
    margin-right: ${theme.spacing[1]};
  }

  /* Right icon */
  > img.${iconRightClassName} {
    margin-left: ${theme.spacing['0_5']};
    transition: transform ${theme.animation.duration.default};
  }
`;
export const clickableListItemStyles = css `
  cursor: pointer;
  &:disabled {
    cursor: default;
  }

  transition: background-color ${theme.animation.duration.default};

  &:not(:disabled) {
    ${focusVisible(`
        outline: 0;
        box-shadow: inset 0 0 0 ${theme.border.width.xs} ${brand.list.color.focus.outline.inner}, inset 0 0 0 ${theme.border.width.s} ${brand.list.color.focus.outline.outer};
      `, `
        box-shadow: none;
      `)}

    &:hover,
    &:active {
      background-color: ${brand.list.color.hover.background.onDark};
    }
  }
`;
const animateIconRightStyles = css `
  &:not(:disabled) {
    /* Move right image on interaction */
    &:active > img.${iconRightClassName} {
      transform: translateX(4px);
    }

    @media (hover: hover) {
      &:hover > img.${iconRightClassName} {
        transform: translateX(8px);
      }
      &:active > img.${iconRightClassName} {
        transform: translateX(12px);
      }
    }
  }
`;
export function rightIconItemStyles(iconRight) {
    return isIconRightDirectional(iconRight) ? animateIconRightStyles : undefined;
}
/**
 * List item component for use as child elements for the `List` component.
 */
export const ListItem = forwardRefAs(function ListItem(props, ref) {
    const { as: Component = 'button', iconLeft, iconRight, iconColor, passive, onWhiteBackground, children, className, ...rest } = props;
    const isDisabled = props.disabled === true;
    const computedIconColor = isDisabled
        ? findColorFromCSSVariable(brand.list.color.disabled.content)
        : iconColor || findColorFromCSSVariable(brand.list.color.default.marker.content);
    const computedTextColor = isDisabled
        ? findColorFromCSSVariable(brand.list.color.disabled.content)
        : passive
            ? findColorFromCSSVariable(brand.list.color.default.item.content)
            : findColorFromCSSVariable(brand.list.color.default.marker.content);
    return (React.createElement(Component, { className: classnames(baseListItemStyles, !passive && clickableListItemStyles, onWhiteBackground &&
            !passive &&
            css `
            &:not(:disabled) {
              &:hover,
              &:active {
                background-color: ${brand.list.color.hover.background.onLight};
              }
            }
          `, !passive && rightIconItemStyles(iconRight), className), ref: ref, ...rest },
        iconLeft && React.createElement(Icon, { name: iconLeft, color: computedIconColor, className: iconLeftClassName }),
        React.createElement(Text, { size: "m", color: computedTextColor }, children),
        iconRight && React.createElement(Icon, { name: iconRight, color: computedIconColor, className: iconRightClassName })));
});


import "../../../css/molecules/List/ListItem.css";
